import React from 'react';

const Features: React.FC = () => {
  return (
    <section id="features" className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="bg-purple-100 text-purple-700 px-4 py-2 rounded-full text-sm font-semibold inline-block mb-4">
            ✨ Alles wat je nodig hebt
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Krachtige features voor jouw succes
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            WFT Study Buddy biedt alles wat je nodig hebt om je examen te halen
          </p>
        </div>

        {/* Features Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {/* Feature 1: AI Question Generation */}
          <div className="bg-white rounded-xl p-8 shadow-md hover:shadow-xl transition-shadow border border-gray-100">
            <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center text-3xl mb-4">
              🤖
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-3">
              AI Vraag Generator
            </h3>
            <p className="text-gray-600 mb-4">
              Onze AI genereert unieke, relevante vragen op basis van officiële WFT syllabus. Nooit meer dezelfde vragen!
            </p>
            <ul className="space-y-2 text-sm text-gray-600">
              <li className="flex items-center space-x-2">
                <span className="text-green-500">✓</span>
                <span>Unlimited AI vragen mogelijk</span>
              </li>
              <li className="flex items-center space-x-2">
                <span className="text-green-500">✓</span>
                <span>3 moeilijkheidsgraden</span>
              </li>
              <li className="flex items-center space-x-2">
                <span className="text-green-500">✓</span>
                <span>Per onderwerp oefenen</span>
              </li>
            </ul>
          </div>

          {/* Feature 2: Weak Points Analysis */}
          <div className="bg-white rounded-xl p-8 shadow-md hover:shadow-xl transition-shadow border border-gray-100">
            <div className="w-14 h-14 bg-gradient-to-br from-red-500 to-pink-600 rounded-lg flex items-center justify-center text-3xl mb-4">
              📊
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-3">
              Zwakke Punten Analyse
            </h3>
            <p className="text-gray-600 mb-4">
              Ontdek automatisch waar je moeite mee hebt en train gericht je zwakke punten voor maximaal resultaat.
            </p>
            <ul className="space-y-2 text-sm text-gray-600">
              <li className="flex items-center space-x-2">
                <span className="text-green-500">✓</span>
                <span>Automatische detectie</span>
              </li>
              <li className="flex items-center space-x-2">
                <span className="text-green-500">✓</span>
                <span>Gerichte oefeningen</span>
              </li>
              <li className="flex items-center space-x-2">
                <span className="text-green-500">✓</span>
                <span>Voortgang tracking</span>
              </li>
            </ul>
          </div>

          {/* Feature 3: Multiple Exams */}
          <div className="bg-white rounded-xl p-8 shadow-md hover:shadow-xl transition-shadow border border-gray-100">
            <div className="w-14 h-14 bg-gradient-to-br from-green-500 to-emerald-600 rounded-lg flex items-center justify-center text-3xl mb-4">
              📚
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-3">
              Alle WFT Examens
            </h3>
            <p className="text-gray-600 mb-4">
              Van Schade Particulier tot Hypotheken - alle examens op één platform met dezelfde krachtige tools.
            </p>
            <ul className="space-y-2 text-sm text-gray-600">
              <li className="flex items-center space-x-2">
                <span className="text-green-500">✓</span>
                <span>WFT Schade Particulier</span>
              </li>
              <li className="flex items-center space-x-2">
                <span className="text-green-500">✓</span>
                <span>WFT Basis</span>
              </li>
              <li className="flex items-center space-x-2">
                <span className="text-green-500">✓</span>
                <span>WFT Hypotheken</span>
              </li>
            </ul>
          </div>

          {/* Feature 4: Progress Tracking */}
          <div className="bg-white rounded-xl p-8 shadow-md hover:shadow-xl transition-shadow border border-gray-100">
            <div className="w-14 h-14 bg-gradient-to-br from-yellow-500 to-orange-600 rounded-lg flex items-center justify-center text-3xl mb-4">
              📈
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-3">
              Voortgang Dashboard
            </h3>
            <p className="text-gray-600 mb-4">
              Zie in één oogopslag hoe je ervoor staat met gedetailleerde statistieken en persoonlijke insights.
            </p>
            <ul className="space-y-2 text-sm text-gray-600">
              <li className="flex items-center space-x-2">
                <span className="text-green-500">✓</span>
                <span>Score geschiedenis</span>
              </li>
              <li className="flex items-center space-x-2">
                <span className="text-green-500">✓</span>
                <span>Topic performance</span>
              </li>
              <li className="flex items-center space-x-2">
                <span className="text-green-500">✓</span>
                <span>Streak tracking</span>
              </li>
            </ul>
          </div>

          {/* Feature 5: Study Materials */}
          <div className="bg-white rounded-xl p-8 shadow-md hover:shadow-xl transition-shadow border border-gray-100">
            <div className="w-14 h-14 bg-gradient-to-br from-indigo-500 to-blue-600 rounded-lg flex items-center justify-center text-3xl mb-4">
              📖
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-3">
              Complete Samenvattingen
            </h3>
            <p className="text-gray-600 mb-4">
              Lees compacte samenvattingen van alle examenstof, perfect voor last-minute herhaling.
            </p>
            <ul className="space-y-2 text-sm text-gray-600">
              <li className="flex items-center space-x-2">
                <span className="text-green-500">✓</span>
                <span>Per hoofdstuk</span>
              </li>
              <li className="flex items-center space-x-2">
                <span className="text-green-500">✓</span>
                <span>Officiële syllabus</span>
              </li>
              <li className="flex items-center space-x-2">
                <span className="text-green-500">✓</span>
                <span>Direct oefenen</span>
              </li>
            </ul>
          </div>

          {/* Feature 6: Mobile Friendly */}
          <div className="bg-white rounded-xl p-8 shadow-md hover:shadow-xl transition-shadow border border-gray-100">
            <div className="w-14 h-14 bg-gradient-to-br from-pink-500 to-rose-600 rounded-lg flex items-center justify-center text-3xl mb-4">
              📱
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-3">
              Overal & Altijd
            </h3>
            <p className="text-gray-600 mb-4">
              Oefen waar en wanneer je wilt - op desktop, tablet of smartphone. Jouw voortgang synct automatisch.
            </p>
            <ul className="space-y-2 text-sm text-gray-600">
              <li className="flex items-center space-x-2">
                <span className="text-green-500">✓</span>
                <span>Responsive design</span>
              </li>
              <li className="flex items-center space-x-2">
                <span className="text-green-500">✓</span>
                <span>Cloud sync</span>
              </li>
              {/* <li className="flex items-center space-x-2">
                <span className="text-green-500">✓</span>
                <span>Offline mode (binnenkort)</span>
              </li> */}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Features;